import { useMemo, useState } from 'react';
import { type SortingState } from '@tanstack/react-table';
import { useAuth } from '../auth/useAuth';
import { useItems } from '../hooks/useItems';
import { useUrlFilters } from '../hooks/useUrlFilters';
import { useSavedPresets } from '../hooks/useSavedPresets';
import { useColumnPrefs } from '../hooks/useColumnPrefs';
import { applyFilters } from '../lib/filters/applyFilters';
import { toCsv, toXlsxBuffer } from '../lib/exporters';
import { FilterToolbar } from '../components/FilterToolbar';
import { SavedPresetsDropdown } from '../components/SavedPresetsDropdown';
import { ItemTable, ALL_COLUMNS, DEFAULT_COLUMNS, HEADERS } from '../components/ItemTable';
import { ItemDrawer } from '../components/ItemDrawer';

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  a.remove(); URL.revokeObjectURL(url);
}

function todayStamp(): string {
  return new Date().toISOString().slice(0, 10);
}

export interface TablePageProps {
  title?: string;
  forceAnchorsOnly?: boolean;
  extraColumn?: 'anchorWhy';
  extraToolbarSlot?: React.ReactNode;
  emptyStateMessage?: React.ReactNode;
}

export function TablePage({ title = 'Table', forceAnchorsOnly = false, extraColumn, extraToolbarSlot, emptyStateMessage }: TablePageProps = {}) {
  const { user } = useAuth();
  const { filters: urlFilters, setFilters } = useUrlFilters();
  const filters = forceAnchorsOnly ? { ...urlFilters, anchorsOnly: true } : urlFilters;
  const { data: itemsAll, isFetching } = useItems(filters.showArchived);
  const userId = user?.userId ?? '_anon';
  const presets = useSavedPresets(userId);
  const defaultCols = useMemo(() => {
    if (extraColumn === 'anchorWhy') return [...DEFAULT_COLUMNS, 'anchorWhy'];
    return DEFAULT_COLUMNS;
  }, [extraColumn]);
  const prefs = useColumnPrefs(userId, defaultCols);
  const [sorting, setSorting] = useState<SortingState>(
    filters.sort.length
      ? filters.sort.map((s) => ({ id: s.field, desc: s.dir === 'desc' }))
      : [{ id: 'wsjf', desc: true }],
  );

  const filtered = useMemo(() => applyFilters(itemsAll ?? [], filters), [itemsAll, filters]);

  const exportCsv = () => {
    const visibleCols = [...prefs.visibleCols].filter((c) => (ALL_COLUMNS as readonly string[]).includes(c));
    const csv = toCsv(filtered, visibleCols);
    downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `flow-portfolio-${todayStamp()}.csv`);
  };
  const exportXlsx = async () => {
    const visibleCols = [...prefs.visibleCols].filter((c) => (ALL_COLUMNS as readonly string[]).includes(c));
    const buf = await toXlsxBuffer(filtered, visibleCols, 'Portfolio');
    downloadBlob(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `flow-portfolio-${todayStamp()}.xlsx`);
  };

  return (
    <div className="flex flex-col gap-3">
      {isFetching && <div className="h-0.5 w-full bg-brand-tint" />}
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold">{title}</h1>
        <span className="text-sm text-slate-500">{filtered.length} {filtered.length === 1 ? 'item' : 'items'}</span>
      </div>
      <FilterToolbar
        filters={filters}
        onChange={setFilters}
        hiddenToggles={forceAnchorsOnly ? ['anchorsOnly'] : []}
      />
      <div className="flex flex-wrap items-center gap-2">
        <SavedPresetsDropdown presets={presets.presets} onSave={presets.save} onRemove={presets.remove} />
        <ColumnVisibilityMenu visibleCols={prefs.visibleCols} setVisibleCols={prefs.setVisibleCols} />
        <DensityToggle density={prefs.density} setDensity={prefs.setDensity} />
        <div className="ml-auto flex items-center gap-1">
          {extraToolbarSlot}
          <button type="button" onClick={exportCsv} className="rounded border border-slate-300 bg-white px-2 py-1 text-xs">Export CSV</button>
          <button type="button" onClick={exportXlsx} className="rounded border border-slate-300 bg-white px-2 py-1 text-xs">Export XLSX</button>
        </div>
      </div>
      <div className="overflow-x-auto rounded border border-slate-200 bg-white">
        {filtered.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">
            {emptyStateMessage ? (
              <>{emptyStateMessage}</>
            ) : itemsAll && itemsAll.length === 0 ? (
              <>No items yet — create one from the kanban.</>
            ) : forceAnchorsOnly ? (
              <>No anchors yet — open any item and flag it, or click ⚓ Add anchor.</>
            ) : (
              <>No items match these filters. <button type="button" onClick={() => setFilters({ ...filters, q: '', category: [], decision: [], lifecycle: [], confidence: [], anchorsOnly: false })} className="underline">Clear filters</button></>
            )}
          </div>
        ) : (
          <ItemTable items={filtered} visibleCols={prefs.visibleCols} density={prefs.density} sorting={sorting} onSortingChange={setSorting} />
        )}
      </div>
      <ItemDrawer />
    </div>
  );
}

function ColumnVisibilityMenu({ visibleCols, setVisibleCols }: { visibleCols: Set<string>; setVisibleCols: (s: Set<string>) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-block text-xs">
      <button type="button" onClick={() => setOpen((v) => !v)} className="rounded border border-slate-300 bg-white px-2 py-1">Columns ▾</button>
      {open && (
        <div className="absolute z-10 mt-1 w-44 rounded border border-slate-200 bg-white p-1 shadow">
          {ALL_COLUMNS.map((c) => (
            <label key={c} className="flex items-center gap-1 px-1 py-0.5">
              <input
                type="checkbox"
                checked={visibleCols.has(c)}
                onChange={(e) => {
                  const next = new Set(visibleCols);
                  if (e.target.checked) next.add(c); else next.delete(c);
                  setVisibleCols(next);
                }}
              />
              <span>{HEADERS[c] ?? c}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function DensityToggle({ density, setDensity }: { density: 'compact' | 'comfortable'; setDensity: (d: 'compact' | 'comfortable') => void }) {
  return (
    <button type="button" onClick={() => setDensity(density === 'compact' ? 'comfortable' : 'compact')} className="rounded border border-slate-300 bg-white px-2 py-1 text-xs">
      {density === 'compact' ? 'Compact' : 'Comfortable'}
    </button>
  );
}
